import { BearerTokenClaims, IBearerTokenClaims } from "../entities-generated/models/authentication/bearer-token-claims";
import { IExtendedClaims } from "../entities-generated/models/authentication/extended-claims";
import { IRefreshTokenClaims, RefreshTokenClaims } from "../entities-generated/models/authentication/refresh-token-claims";
import { ITokenSet } from "../entities-generated/models/authentication/token-set";
import { LocalStorageService } from "../framework/services/local-storage";
import { isNullOrWhitespace, isThisLocalhost } from "../framework/utils";
import { getUtcNowUnixTimestamp, SECOND_IN_MILLISECONDS } from "../framework/utils/date-time";
import { AuthenticationApiClient } from "../services/authentication";
import { REFRESH_TOKEN_KEY } from "../services/refresh-api-client";

export interface IRefreshToken {
  token: string;
  claims: IRefreshTokenClaims;
}

export interface IBearerToken {
  token: string;
  claims: IBearerTokenClaims & IExtendedClaims;
  schedulableAssignmentPosts: string[];
  schedulableVolunteerCongregations: string[];
  enterableVolunteerCongregations: string[];
}

class CurrentUserServiceClass {

  public initializeService (isOversightApp: boolean) {
    this._isOversightApp = isOversightApp;
  }

  public getRefreshToken () {
    return this.retrieveRefreshToken()
    .then(refresh => {
      !!refresh && this.storeRefreshToken(refresh);
      return refresh;
    });
  }
  
  public getBearerToken () {
    if(!!this._bearerToken) {
      const now = getUtcNowUnixTimestamp();
      if(now < (this._bearerToken.claims.tokenExpiration - (20 * SECOND_IN_MILLISECONDS))) {
        return Promise.resolve(this._bearerToken);
      } else {
        this.clearBearerToken();
      }
    }

    return this.fetchBearerToken();
  }

  public isSignedIn () {
    if(!!this._bearerToken) {
      const now = getUtcNowUnixTimestamp();
      if(now < (this._bearerToken.claims.tokenExpiration - (20 * SECOND_IN_MILLISECONDS))) {
        return true;
      }
    }

    return false;
  }

  public signOut (signOutAllDevices: boolean) {
    this.clearBearerToken();
    this.clearRefreshToken();

    return Promise.resolve();
  }

  private clearRefreshToken() {
    LocalStorageService.clear(REFRESH_TOKEN_KEY);
    this._refreshToken = null;
  }

  private clearBearerToken() {
    this._bearerToken = null;
  }

  private parseRefreshToken (token: string) {
    const json = atob(token.split('.')[1]);
    const map = JSON.parse(json) as { [key: string]: string };
    const claims = new RefreshTokenClaims();
    claims.id = map['id'];
    claims.tokenId = map['tokenId'];
    claims.domainId = map['domainId'];
    claims.role = parseInt(map['role']);
    claims.tokenExpiration = parseInt(map['tokenExpiration']);
    return claims;
  }

  private parseBearerToken (token: string) {
    const json = atob(token.split('.')[1]);
    const map = JSON.parse(json) as { [key: string]: string };
    const claims = new BearerTokenClaims();
    claims.id = map['id'];
    claims.domainId = map['domainId'];
    claims.role = parseInt(map['role']);
    claims.email = map['email'];
    claims.schedulableAssignmentPosts = map['schedulableAssignmentPosts'];
    claims.schedulableVolunteerCongregations = map['schedulableVolunteerCongregations'];
    claims.enterableVolunteerCongregations = map['enterableVolunteerCongregations'];
    claims.allowCheckIn = map['allowCheckIn'] === 'true';
    claims.allowAssignmentPostEditing = map['allowAssignmentPostEditing'] === 'true';
    claims.tokenExpiration = parseInt(map['tokenExpiration']);
    return claims;
  }

  private storeRefreshToken (refresh: IRefreshToken) {
    LocalStorageService.set(REFRESH_TOKEN_KEY, refresh.token);
    this._refreshToken = refresh;
  }

  private storeBearerToken (claims: IBearerTokenClaims, xClaims: IExtendedClaims, token: string) {
    const bearer: IBearerToken = {
      token,
      claims: {...claims, ...xClaims},
      schedulableAssignmentPosts: JSON.parse(claims.schedulableAssignmentPosts) as string[],
      schedulableVolunteerCongregations: JSON.parse(claims.schedulableVolunteerCongregations) as string[],
      enterableVolunteerCongregations: JSON.parse(claims.enterableVolunteerCongregations) as string[]
    };
    this._bearerToken = bearer;

    return bearer;
  }

  private getRefreshTokenFromStorage (): IRefreshToken | null {
    const token = LocalStorageService.get(REFRESH_TOKEN_KEY) as string | null;
    if(!token) {
      this.clearRefreshToken();
      return null;
    }

    const claims = this.parseRefreshToken(token);
    const now = getUtcNowUnixTimestamp();
    // we'll compare to a 20s less expiration, to prevent last-second expiration
    if ((claims.tokenExpiration - (20 * SECOND_IN_MILLISECONDS)) < now) {
      this.clearBearerToken();
      return null;
    }

    return { token, claims };
  }

  private getRefreshTokenFromPage (): IRefreshToken | null {
    const token = window.document.querySelector('input#refresh-token')?.getAttribute('value') ?? '';
    if(isNullOrWhitespace(token) || token === '{refresh_token}') {
      return null;
    }

    const claims = this.parseRefreshToken(token);
    const now = getUtcNowUnixTimestamp();
    // we'll compare to a 20s less expiration, to prevent last-second expiration
    if ((claims.tokenExpiration - (20 * SECOND_IN_MILLISECONDS)) < now) {
      return null;
    }

    return { token, claims };
  }

  private getDevRefreshToken (): Promise<IRefreshToken | null> {
    if(isThisLocalhost() === false) {
      return Promise.resolve(null);
    }

    const onGotToken = (resp: ITokenSet) => {
      const token = resp.refreshToken;
      const claims = this.parseRefreshToken(token);
      return { token, claims };
    }

    if(this._isOversightApp) {
      return AuthenticationApiClient.devRefreshOversightApiTokens()
      .then(onGotToken)
      .catch(() => null);
    } else {
      return AuthenticationApiClient.devRefreshOversightVolunteerApiTokens()
      .then(onGotToken)
      .catch(() => null);
    }
  }

  private retrieveRefreshToken () {
    if(!!this._refreshToken) {
      const now = getUtcNowUnixTimestamp();
      if(now < (this._refreshToken.claims.tokenExpiration - (20 * SECOND_IN_MILLISECONDS))) {
        return Promise.resolve(this._refreshToken);
      } else {
        this.clearRefreshToken();
        return Promise.resolve(null);
      }
    }

    let rt = this.getRefreshTokenFromStorage();
    if(!!rt) {
      return Promise.resolve(rt);
    }

    rt = this.getRefreshTokenFromPage();
    if(!!rt) {
      return Promise.resolve(rt);
    }

    return this.getDevRefreshToken();
  }

  private fetchBearerToken () {
    return this.getRefreshToken()
    .then(refreshToken => {
      if(!!refreshToken) {
        return AuthenticationApiClient.refreshApiTokens()
      } else {
        return null;
      }
    })
    .then(resp => {
      if(!resp) { return null; }

      const refresh: IRefreshToken = {
        token: resp.refreshToken,
        claims: this.parseRefreshToken(resp.refreshToken)
      };
      this.storeRefreshToken(refresh);

      const bearerClaims = this.parseBearerToken(resp.bearerToken);
      const bearer = this.storeBearerToken(bearerClaims, resp.extendedClaims, resp.bearerToken);

      return bearer;
    })
    .catch(() => {
      this.clearRefreshToken();
      return null;
    });
  }

  private _isOversightApp: boolean = false;
  private _bearerToken: IBearerToken | null = null;
  private _refreshToken: IRefreshToken | null = null;
}

export const CurrentUserService = new CurrentUserServiceClass();
